"use client";

import Image from "next/image";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { useTranslations } from "next-intl";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import { COMPANY } from "@/lib/company";
import type { Locale } from "@/lib/i18n";
import { PLAYGROUND_LANGUAGE_LINKS } from "@/features/playground/config/language-links";
import { IntentLink } from "./site-nav/IntentLink";

type Props = { locale: Locale; siteName?: string; tagline?: string; compact?: boolean };

// About thirty links reach the screen together when a visitor scrolls to the
// bottom, and a plain `<Link>` would start that many page downloads right then.
// They fetch on intent instead, like the site bar's (`SITE-NAV01`).

export function Footer({ locale, siteName = "Progsity", tagline, compact = false }: Props) {
  const year = new Date().getFullYear();
  const t = useTranslations("footer");
  const taglineText = tagline?.trim() ? tagline.trim() : t("tagline");

  const columns = [
    {
      heading: t("platform"),
      links: [
        { label: t("platformLinks.courses"), href: `/${locale}/courses` },
        { label: t("platformLinks.learn"), href: `/${locale}/learn` },
        { label: t("platformLinks.quizExams"), href: `/${locale}/quiz-exams` },
        { label: t("platformLinks.careerPath"), href: `/${locale}/career` },
        { label: t("platformLinks.programs"), href: `/${locale}/programs` },
        { label: t("platformLinks.judge"), href: `/${locale}/judge` },
        { label: t("platformLinks.contests"), href: `/${locale}/contests` },
        { label: t("platformLinks.hostContest"), href: `/${locale}/contests/host` },
        { label: t("platformLinks.playground"), href: `/${locale}/playground` },
        { label: t("platformLinks.faq"), href: `/${locale}#faq` },
      ],
    },
    {
      heading: t("formats"),
      links: [
        {
          label: t("formatLinks.liveCourses"),
          href: `/${locale}/courses?format=live-recorded`,
        },
        {
          label: t("formatLinks.recordedCourses"),
          href: `/${locale}/courses?format=recorded`,
        },
        { label: t("formatLinks.bootcamps"), href: `/${locale}/programs` },
        {
          label: t("formatLinks.institutionalTraining"),
          href: `/${locale}/for-institutions`,
        },
      ],
    },
    {
      heading: t("communityColumn"),
      links: [
        { label: t("community.ambassadorProgram"), href: `/${locale}/ambassador` },
        { label: t("community.events"), href: `/${locale}/events` },
      ],
    },
    {
      heading: t("companyColumn"),
      links: [
        { label: t("company.about"), href: `/${locale}/about` },
        { label: t("company.helpCenter"), href: `/${locale}/help` },
        { label: t("company.contact"), href: `/${locale}/contact` },
        { label: t("company.forInstitutions"), href: `/${locale}/for-institutions` },
        { label: t("company.policies"), href: `/${locale}/policies` },
      ],
    },
  ];

  // `SITE-SPD02`: the footer is laid out only when the visitor scrolls near it.
  // On a phone it is below the first screen of every page, and its 97 elements
  // (153 Bengali letters on /bn, drawn in the system's fallback font) were part
  // of the layout every first paint waited for: skipping it took /bn's first
  // paint 0.31 s earlier on the phone profile. The stand-in heights are its own
  // (about 1,500 px on a phone, 600 px from `lg`); `auto` keeps the real one
  // after it has rendered once. A short page that shows it at load renders it
  // at once, like any band (`components/showcase/below-fold.ts`).
  return (
    <footer className="border-t border-[rgb(var(--border))] bg-[rgb(var(--surface-strong))] [content-visibility:auto] [contain-intrinsic-size:auto_1500px] lg:[contain-intrinsic-size:auto_600px]">
      <Container className={compact ? "py-8" : "py-14"}>
        <div className={cn(compact ? "grid grid-cols-2 gap-x-6 gap-y-8" : "grid gap-10", "md:grid-cols-[minmax(0,1.4fr)_repeat(2,minmax(0,1fr))] lg:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))]")}>
          {/* Brand */}
          <div className={compact ? "col-span-2 md:col-span-1" : undefined}>
            <IntentLink href={`/${locale}`} aria-label={siteName}>
              <Image
                src="/logo-white.svg"
                alt={siteName}
                width={138}
                height={34}
                className="h-8 w-auto"
              />
            </IntentLink>
            <p className="mt-4 max-w-[220px] text-sm leading-6 text-white/60">
              {taglineText}
            </p>
            <address className="mt-6 flex max-w-[260px] flex-col gap-2.5 text-sm not-italic leading-6 text-white/60">
              <p className="flex items-start gap-2.5">
                <MapPin className="mt-1.5 h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden="true" />
                <span>
                  <span className="sr-only">{t("contact.addressLabel")}: </span>
                  {COMPANY.addressLines[0]}
                  <br />
                  {COMPANY.addressLines[1]}
                </span>
              </p>
              <p className="flex items-center gap-2.5">
                <Phone className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden="true" />
                <a href={`tel:${COMPANY.phoneTel}`} className="transition-colors hover:text-white">
                  <span className="sr-only">{t("contact.phoneLabel")}: </span>
                  {COMPANY.phoneDisplay}
                </a>
              </p>
              <p className="flex items-center gap-2.5">
                <Mail className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden="true" />
                <a
                  href={`mailto:${COMPANY.emails.support}`}
                  className="transition-colors hover:text-white"
                >
                  <span className="sr-only">{t("contact.emailLabel")}: </span>
                  {COMPANY.emails.support}
                </a>
              </p>
            </address>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <div key={col.heading}>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
                {col.heading}
              </p>
              <ul className={cn("mt-4 flex flex-col", compact ? "gap-1" : "gap-2.5")}>
                {col.links.map((link) => (
                  <li key={`${link.href}:${link.label}`}>
                    <IntentLink
                      href={link.href}
                      className="text-sm text-white/60 transition-colors hover:text-white"
                    >
                      {link.label}
                    </IntentLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* The ten language playgrounds, one row (`GR-PLG01`): the site's only
            crawl path to `/playground/<language>` outside the pages themselves. */}
        <nav
          aria-label={t("compilersRow")}
          className={cn(
            "flex flex-wrap items-baseline gap-x-3 gap-y-1.5 border-t border-white/10 pt-4 text-sm",
            compact ? "mt-6" : "mt-12",
          )}
        >
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
            {t("compilersRow")}
          </span>
          {PLAYGROUND_LANGUAGE_LINKS.map((lang) => (
            <IntentLink
              key={lang.id}
              href={`/${locale}/playground/${lang.seoSlug}`}
              className="text-white/60 transition-colors hover:text-white"
            >
              {lang.name}
            </IntentLink>
          ))}
        </nav>

        {/* Bottom bar */}
        <div className={cn("border-t border-white/10 pt-4 text-xs leading-5", compact ? "mt-6" : "mt-8")}>
          <p className={cn("text-sm font-medium text-white/80", !compact && "mt-4")}>
            {t("copyright", { year: String(year), company: COMPANY.legalName })}
          </p>
          <p className="mt-1 text-white/60">
            {t.rich("productOf", {
              company: COMPANY.legalName,
              regNo: COMPANY.registrationNo,
              link: (chunks) => (
                <a
                  className="inline-flex items-center gap-0.5 font-semibold text-white underline decoration-white/35 underline-offset-[3px] transition-colors hover:decoration-white"
                  href={COMPANY.website}
                  rel="noopener"
                  target="_blank"
                >
                  {chunks}
                  <ArrowUpRight aria-hidden="true" className="h-3 w-3" />
                </a>
              ),
            })}
          </p>
        </div>
      </Container>
    </footer>
  );
}
